// The whole product's map, in one place: the sidebar draws it, the quick
// search jumps through it, the top bar names where you are from it.
//
// Klaviyo's information architecture, our words: what a marketer does every
// day first, what keeps the sending healthy second, the numbers last.

import {
  ActivityIcon,
  BanIcon,
  ChartColumnIcon,
  FilterIcon,
  FlameIcon,
  Grid3x3Icon,
  IdCardIcon,
  LayoutDashboardIcon,
  LayoutTemplateIcon,
  MailCheckIcon,
  RadarIcon,
  SendIcon,
  SettingsIcon,
  UserRoundIcon,
  UsersIcon,
  WandSparklesIcon,
  WorkflowIcon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Only active on this exact path, not under it. */
  end?: boolean;
  /** Shown, not reachable: the slice that builds it has not landed. */
  soon?: boolean;
  /** Extra words the quick search matches. */
  keywords?: string;
  /** Only an admin sees it, in the sidebar and in the quick search (4.5). */
  adminOnly?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  {
    label: "Email marketing",
    items: [
      { to: "/kampanyok", label: "Kampányok", icon: SendIcon, keywords: "campaign kiküldés" },
      {
        to: "/flowk",
        label: "Flow-k",
        icon: WorkflowIcon,
        keywords: "flow automatizmus klaviyo welcome kosár checkout browse árnyék",
      },
      { to: "/sablonok", label: "Sablonok", icon: LayoutTemplateIcon, keywords: "template levél mjml" },
      { to: "/szegmensek", label: "Szegmensek", icon: FilterIcon, keywords: "segment közönség" },
      { to: "/kontaktok", label: "Kontaktok", icon: UsersIcon, keywords: "contact profil cím" },
      { to: "/kizarasok", label: "Kizárások", icon: BanIcon, keywords: "suppression tiltólista leiratkozás" },
    ],
  },
  {
    label: "Kiküldés",
    items: [
      { to: "/", label: "Áttekintés", icon: LayoutDashboardIcon, end: true, keywords: "home kezdőlap" },
      { to: "/kezbesites", label: "Kézbesítés", icon: MailCheckIcon, keywords: "deliverability bounce panasz digest" },
      { to: "/orszem", label: "Őrszem", icon: RadarIcon, keywords: "watchdog postmaster snds pool" },
      {
        to: "/kuldo-fiokok",
        label: "Országbeállítások",
        icon: IdCardIcon,
        keywords:
          "sending account küldő-fiók beállítás ország feladó lábléc cégadat székhely adószám weboldal site követő tracking domain leiratkozás fiók pool ses",
      },
      {
        to: "/warmup",
        label: "Warmup",
        icon: FlameIcon,
        keywords: "bemelegítés pool keret limit pause megállítás feloldás rámpa görbe ip",
      },
    ],
  },
  {
    label: "Adat",
    items: [
      { to: "/riportok", label: "Riportok", icon: ChartColumnIcon, end: true, keywords: "report" },
      { to: "/riportok/pivot", label: "Riportok · pivot", icon: Grid3x3Icon, keywords: "pivot tag" },
      { to: "/esemenyek", label: "Események", icon: ActivityIcon, keywords: "webshop event kosár rendelés" },
    ],
  },
  {
    label: "Rendszer",
    items: [
      {
        to: "/beallitasok",
        label: "Beállítások",
        icon: SettingsIcon,
        adminOnly: true,
        keywords:
          "settings felhasználó user meghívás jogosultság szerep admin marketing letiltás jelszó jelentés reggeli levél digest vezetőség címzett",
      },
    ],
  },
];

/** Places the quick search can go that are not in the sidebar. */
export const EXTRA_DESTINATIONS: (NavItem & { group: string })[] = [
  {
    to: "/kampanyok/uj",
    label: "Új kampány",
    icon: WandSparklesIcon,
    group: "Email marketing",
    keywords: "kampány-varázsló létrehozás több ország wizard",
  },
  {
    to: "/profilom",
    label: "Profilom",
    icon: UserRoundIcon,
    group: "Fiók",
    keywords: "profil név jelszó csere fiók",
  },
];

/** The map as this user may see it: an admin-only item is left out for
 * everybody else, and a group with nothing left goes with it. The API refuses
 * them anyway (403); this keeps a marketer from being led to a closed door. */
export function visibleNav(isAdmin: boolean): NavGroup[] {
  return NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => isAdmin || !item.adminOnly),
  })).filter((group) => group.items.length > 0);
}

export interface Place {
  group: string;
  item: NavItem;
}

/** Where a path sits in the map: the longest nav path that contains it. */
export function placeOf(pathname: string): Place | null {
  let best: Place | null = null;
  for (const group of NAV) {
    for (const item of group.items) {
      if (item.soon) continue;
      const matches = item.end
        ? pathname === item.to
        : pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (matches && (best === null || item.to.length > best.item.to.length)) {
        best = { group: group.label, item };
      }
    }
  }
  return best;
}
